import { useCallback, useState } from 'react';
import {
  AnalisisRiesgo,
  Fase,
  Peligro,
  RiesgoEvaluacion,
} from '../types/AnalisisRiesgo';
import { save, findAll } from '../services/Service';
import { useRiesgos } from './useRiesgos';
import { logger } from '../utils/logger';

export const useAnalisisRiesgo = () => {
  // Usar el hook de riesgos para obtener peligros personales
  const { peligrosPersonales } = useRiesgos();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [analisisRiesgoList, setAnalisisRiesgoList] = useState<
    AnalisisRiesgo[]
  >([]);
  const [analisisRiesgoSelected, setAnalisisRiesgoSelected] =
    useState<AnalisisRiesgo>({
      id: 0,
      idSolicitud: 0,
      fases: [],
    });

  // Separar las fases por tipo
  const [fasesPersonales, setFasesPersonales] = useState<Fase[]>([
    {
      id: 1,
      nroFase: 1,
      descripcion: '',
      peligros: [], // Inicializar vacío, se agregan peligros via botón "Agregar Peligro"
      evaluacionRiesgo: {
        exposicion: 0,
        probabilidad: 0,
        consecuencia: 0,
        riesgo: 0,
        nivelRiesgo: 'MENOR',
      },
    },
  ]);

  const [fasesEntorno, setFasesEntorno] = useState<Fase[]>([
    {
      id: 1,
      nroFase: 1,
      descripcion: '',
      peligros: [],
      evaluacionRiesgo: {
        exposicion: 0,
        probabilidad: 0,
        consecuencia: 0,
        riesgo: 0,
        nivelRiesgo: 'MENOR',
      },
    },
  ]);

  // Estado para manejar los checkboxes de peligros de entorno
  const [peligroCheckStatesEntorno, setPeligroCheckStatesEntorno] =
    useState<{
      [faseId: number]: { [peligroId: number]: boolean };
    }>({});

  // Para mantener compatibilidad, las fases por defecto serán las personales
  const [fases, setFases] = useState<Fase[]>(fasesPersonales);

  const [peligroSelected, setPeligroSelected] = useState<Peligro>({
    id: 0,
    nombre: '',
    barreraAdministrativa: '',
    barreraFisica: '',
    eppEspecifico: '',
  });

  const calcularRiesgo = useCallback(
    (
      exposicion: number,
      probabilidad: number,
      consecuencia: number
    ) => {
      const riesgo = exposicion * probabilidad * consecuencia;
      let nivelRiesgo = '';

      if (riesgo <= 14) {
        nivelRiesgo = 'MENOR';
      } else if (riesgo > 14 && riesgo <= 35) {
        nivelRiesgo = 'MODERADO';
      } else if (riesgo > 35 && riesgo <= 82) {
        nivelRiesgo = 'ALTO';
      } else if (riesgo > 82 && riesgo <= 350) {
        nivelRiesgo = 'URGENTE';
      } else {
        nivelRiesgo = 'EXTREMO';
      }

      return { riesgo, nivelRiesgo };
    },
    []
  );

  const actualizarRiesgoEvaluacion = useCallback(
    (
      faseId: number,
      campo: keyof RiesgoEvaluacion,
      valor: number,
      tipo: 'PERSONAL' | 'ENTORNO' = 'PERSONAL'
    ) => {
      if (tipo === 'PERSONAL') {
        const nuevasFases = fasesPersonales.map((fase) => {
          if (fase.id === faseId) {
            const nuevaEvaluacion = {
              ...fase.evaluacionRiesgo,
              [campo]: valor,
            };
            const resultado = calcularRiesgo(
              nuevaEvaluacion.exposicion,
              nuevaEvaluacion.probabilidad,
              nuevaEvaluacion.consecuencia
            );
            return {
              ...fase,
              evaluacionRiesgo: {
                ...nuevaEvaluacion,
                riesgo: resultado.riesgo,
                nivelRiesgo: resultado.nivelRiesgo,
              },
            };
          }
          return fase;
        });

        setFasesPersonales(nuevasFases);
        setFases(nuevasFases); // Actualizar también las fases principales
      } else {
        const nuevasFases = fasesEntorno.map((fase) => {
          if (fase.id === faseId) {
            const nuevaEvaluacion = {
              ...fase.evaluacionRiesgo,
              [campo]: valor,
            };
            const resultado = calcularRiesgo(
              nuevaEvaluacion.exposicion,
              nuevaEvaluacion.probabilidad,
              nuevaEvaluacion.consecuencia
            );
            return {
              ...fase,
              evaluacionRiesgo: {
                ...nuevaEvaluacion,
                riesgo: resultado.riesgo,
                nivelRiesgo: resultado.nivelRiesgo,
              },
            };
          }
          return fase;
        });

        setFasesEntorno(nuevasFases);
      }
    },
    [calcularRiesgo, fasesPersonales, fasesEntorno]
  );

  const agregarFase = useCallback(
    (
      descripcion: string,
      tipo: 'PERSONAL' | 'ENTORNO' = 'PERSONAL'
    ) => {
      if (tipo === 'PERSONAL') {
        const nuevaFase: Fase = {
          id: fasesPersonales.length + 1,
          nroFase: fasesPersonales.length + 1,
          descripcion: descripcion,
          peligros: [],
          evaluacionRiesgo: {
            exposicion: 0,
            probabilidad: 0,
            consecuencia: 0,
            riesgo: 0,
            nivelRiesgo: 'MENOR',
          },
        };

        const nuevasFases = [...fasesPersonales, nuevaFase];
        setFasesPersonales(nuevasFases);
        setFases(nuevasFases); // Actualizar también las fases principales
      } else {
        const nuevaFase: Fase = {
          id: fasesEntorno.length + 1,
          nroFase: fasesEntorno.length + 1,
          descripcion: descripcion,
          peligros: [],
          evaluacionRiesgo: {
            exposicion: 0.3,
            probabilidad: 0.6,
            consecuencia: 7,
            riesgo: 1.26,
            nivelRiesgo: 'MENOR',
          },
        };

        const nuevasFases = [...fasesEntorno, nuevaFase];
        setFasesEntorno(nuevasFases);
      }
    },
    [fasesPersonales, fasesEntorno]
  );

  const editarFase = useCallback(
    (
      id: number,
      nuevaDescripcion: string,
      tipo: 'PERSONAL' | 'ENTORNO' = 'PERSONAL'
    ) => {
      if (tipo === 'PERSONAL') {
        const nuevasFases = fasesPersonales.map((fase) =>
          fase.id === id
            ? { ...fase, descripcion: nuevaDescripcion }
            : fase
        );

        setFasesPersonales(nuevasFases);
        setFases(nuevasFases); // Actualizar también las fases principales
      } else {
        const nuevasFases = fasesEntorno.map((fase) =>
          fase.id === id
            ? { ...fase, descripcion: nuevaDescripcion }
            : fase
        );

        setFasesEntorno(nuevasFases);
      }
    },
    [fasesPersonales, fasesEntorno]
  );

  const eliminarFase = useCallback(
    (id: number, tipo: 'PERSONAL' | 'ENTORNO' = 'PERSONAL') => {
      if (tipo === 'PERSONAL') {
        const filtradas = fasesPersonales
          .filter((fase) => fase.id !== id)
          .map((fase, index) => ({ ...fase, nroFase: index + 1 }));
        setFasesPersonales(filtradas);
        setFases(filtradas);
      } else {
        const filtradas = fasesEntorno
          .filter((fase) => fase.id !== id)
          .map((fase, index) => ({ ...fase, nroFase: index + 1 }));
        setFasesEntorno(filtradas);
        // Limpiar los estados de checkbox de la fase eliminada
        setPeligroCheckStatesEntorno((prev) => {
          const nuevo = { ...prev };
          delete nuevo[id];
          return nuevo;
        });
      }
    },
    [fasesPersonales, fasesEntorno, setPeligroCheckStatesEntorno]
  );

  const agregarPeligro = useCallback(
    (
      faseId: number,
      peligro: Omit<Peligro, 'id'>,
      tipo: 'PERSONAL' | 'ENTORNO' = 'PERSONAL'
    ) => {
      if (tipo === 'PERSONAL') {
        const nuevasFases = fasesPersonales.map((fase) => {
          if (fase.id === faseId) {
            // Buscar el peligro en el backend para usar su ID
            const peligroBackend = peligrosPersonales.find(
              (p) => p.nombre === peligro.nombre
            );

            const nuevoPeligro: Peligro = {
              ...peligro,
              id: peligroBackend?.id || fase.peligros.length + 1, // Usar ID del backend o generar uno nuevo
            };
            logger.log('Agregando peligro personal, id:', nuevoPeligro?.id);
            logger.log('Peligro del backend encontrado, id:', peligroBackend?.id);
            return {
              ...fase,
              peligros: [...fase.peligros, nuevoPeligro],
            };
          }
          return fase;
        });

        logger.log('Nuevas fases personales, cantidad:', nuevasFases?.length);
        setFasesPersonales(nuevasFases);
        setFases(nuevasFases);
        logger.log('Estado actualizado - fasesPersonales');
      } else {
        const nuevasFases = fasesEntorno.map((fase) => {
          if (fase.id === faseId) {
            const nuevoPeligro: Peligro = {
              ...peligro,
              id: fase.peligros.length + 1,
            };
            logger.log('Agregando peligro entorno, id:', nuevoPeligro?.id);
            return {
              ...fase,
              peligros: [...fase.peligros, nuevoPeligro],
            };
          }
          return fase;
        });

        logger.log('Nuevas fases entorno, cantidad:', nuevasFases?.length);
        setFasesEntorno(nuevasFases);
        logger.log('Estado actualizado - fasesEntorno');
      }

      logger.log(`=== FIN AGREGAR PELIGRO ===`);
    },
    [fasesPersonales, fasesEntorno, peligrosPersonales]
  );

  const editarPeligro = useCallback(
    (
      faseId: number,
      peligroId: number,
      peligroActualizado: Partial<Omit<Peligro, 'id'>>,
      tipo: 'PERSONAL' | 'ENTORNO' = 'PERSONAL'
    ) => {
      if (tipo === 'PERSONAL') {
        const nuevasFases = fasesPersonales.map((fase) => {
          if (fase.id === faseId) {
            return {
              ...fase,
              peligros: fase.peligros.map((peligro) =>
                peligro.id === peligroId
                  ? { ...peligro, ...peligroActualizado }
                  : peligro
              ),
            };
          }
          return fase;
        });

        logger.log('Nuevas fases personales después de editar, cantidad:', nuevasFases?.length);
        setFasesPersonales(nuevasFases);
        setFases(nuevasFases); // Actualizar también las fases principales
      } else {
        const nuevasFases = fasesEntorno.map((fase) => {
          if (fase.id === faseId) {
            return {
              ...fase,
              peligros: fase.peligros.map((peligro) =>
                peligro.id === peligroId
                  ? { ...peligro, ...peligroActualizado }
                  : peligro
              ),
            };
          }
          return fase;
        });

        logger.log('Nuevas fases entorno después de editar, cantidad:', nuevasFases?.length);
        setFasesEntorno(nuevasFases);
      }

      logger.log(`=== FIN EDITAR PELIGRO ===`);
    },
    [fasesPersonales, fasesEntorno]
  );

  const eliminarPeligro = useCallback(
    (faseId: number, peligroId: number) => {
      setFases((prev) =>
        prev.map((fase) => {
          if (fase.id === faseId) {
            return {
              ...fase,
              peligros: fase.peligros.filter(
                (peligro) => peligro.id !== peligroId
              ),
            };
          }
          return fase;
        })
      );
    },
    []
  );

  const buscarAnalisisRiesgoByIdSolicitud = useCallback(
    async (idSolicitud: number) => {
      try {
        // TODO: Implementar llamada a la API
        logger.log(
          'Buscando análisis de riesgo para solicitud:',
          idSolicitud
        );
        // const response = await api.get(`/analisis-riesgo/solicitud/${idSolicitud}`);
        // setAnalisisRiesgoList(response.data);
      } catch (error) {
        logger.error('Error al buscar análisis de riesgo:', logger.safeError(error));
      }
    },
    []
  );

  const saveOrUpdateAnalisisRiesgo = useCallback(
    async (path: string, analisisRiesgo: any) => {
      try {
        logger.log('Guardando análisis de riesgo en:', path);
        const response = await save(path, analisisRiesgo);
        return response;
      } catch (error) {
        logger.error('Error al guardar análisis de riesgo:', logger.safeError(error));
        throw error;
      }
    },
    []
  );

  const handleAnalisisRiesgoSelected = useCallback(
    (analisisRiesgo: AnalisisRiesgo) => {
      setAnalisisRiesgoSelected(analisisRiesgo);
    },
    []
  );

  const handlePeligroSelected = useCallback((peligro: Peligro) => {
    setPeligroSelected(peligro);
  }, []);

  const eliminarPeligroById = useCallback(
    async (faseId: number, id: string) => {
      try {
        logger.log('Eliminando peligro:', id, 'de fase:', faseId);

        // TODO: Implementar llamada a la API cuando esté disponible
        // const response = await api.delete(`/peligro/${id}`);

        // Elimina solo la primera coincidencia por id. Como el id del peligro
        // corresponde al del catálogo, datos previos podrían contener filas
        // duplicadas con el mismo id; filtrar por id eliminaría todas, por eso
        // se remueve únicamente la ocurrencia indicada.
        const eliminarUnaOcurrencia = (peligros: Peligro[]) => {
          const index = peligros.findIndex(
            (p) => p.id.toString() === id
          );
          if (index === -1) {
            return peligros;
          }
          return peligros.filter((_, i) => i !== index);
        };

        // Actualizar fasesPersonales (que es el estado principal para EvaluacionRiesgosPersonales)
        setFasesPersonales((prev) =>
          prev.map((fase) => {
            if (fase.id === faseId) {
              const nuevasFases = {
                ...fase,
                peligros: eliminarUnaOcurrencia(fase.peligros),
              };
              logger.log('Fase actualizada, peligros restantes:', nuevasFases.peligros?.length);
              return nuevasFases;
            }
            return fase;
          })
        );

        // También actualizar el estado general de fases para mantener consistencia
        setFases((prev) =>
          prev.map((fase) => {
            if (fase.id === faseId) {
              return {
                ...fase,
                peligros: eliminarUnaOcurrencia(fase.peligros),
              };
            }
            return fase;
          })
        );

        logger.log('Peligro eliminado exitosamente');
        return true;
      } catch (error) {
        logger.error('Error al eliminar peligro:', logger.safeError(error));
        throw error;
      }
    },
    [setFasesPersonales, setFases]
  );

  const saveOrUpdatePeligro = useCallback(
    async (faseId: number, endpoint: string, peligroData: any) => {
      try {
        // TODO: Implementar llamada a la API
        logger.log('Guardando peligro:', endpoint, 'en fase:', faseId);
        // const response = await api.post(`/${endpoint}`, peligroData);
        // return response.data;

        // Simulación temporal
        if (peligroData.id === 0) {
          // Nuevo peligro
          setFases((prev) =>
            prev.map((fase) => {
              if (fase.id === faseId) {
                const nuevoPeligro = {
                  ...peligroData,
                  id: fase.peligros.length + 1,
                };
                return {
                  ...fase,
                  peligros: [...fase.peligros, nuevoPeligro],
                };
              }
              return fase;
            })
          );
          return { data: { ...peligroData, id: Date.now() } };
        } else {
          // Actualizar existente
          setFases((prev) =>
            prev.map((fase) => {
              if (fase.id === faseId) {
                return {
                  ...fase,
                  peligros: fase.peligros.map((p) =>
                    p.id === peligroData.id
                      ? { ...p, ...peligroData }
                      : p
                  ),
                };
              }
              return fase;
            })
          );
          return { data: peligroData };
        }
      } catch (error) {
        logger.error('Error al guardar peligro:', logger.safeError(error));
        throw error;
      }
    },
    []
  );

  // Función para guardar el análisis de riesgo completo
  const guardarAnalisisRiesgo = useCallback(
    async (
      idSolicitud: number,
      peligroCheckStates: any,
      observaciones?: string
    ) => {
      try {
        logger.log(`=== DEBUG GUARDAR ANALISIS RIESGO ===`);
        logger.log('FasesPersonales del estado, cantidad:', fasesPersonales?.length);
        logger.log('FasesEntorno del estado, cantidad:', fasesEntorno?.length);
        logger.log('PeligroCheckStates, claves:', Object.keys(peligroCheckStates ?? {}).length);

        // Log adicional para verificar el contenido específico
        logger.log('Detalle fasesPersonales:');
        fasesPersonales.forEach((fase, index) => {
          logger.log(`  Fase ${index + 1} (id: ${fase.id}):`, {
            descripcion: fase.descripcion,
            cantidadPeligros: fase.peligros.length,
            peligros: fase.peligros,
          });
        });

        logger.log('Detalle fasesEntorno:');
        fasesEntorno.forEach((fase, index) => {
          logger.log(`  Fase ${index + 1} (id: ${fase.id}):`, {
            descripcion: fase.descripcion,
            cantidadPeligros: fase.peligros.length,
            peligros: fase.peligros,
          });
        });

        // Preparar fases personales
        const fasesPersonalesData = fasesPersonales.map((fase) => ({
          ...fase,
          tipo: 'PERSONAL' as const,
        }));

        // Preparar fases de entorno con peligros seleccionados
        const fasesEntornoData = fasesEntorno.map((fase) => {
          const peligrosSeleccionados = fase.peligros.filter(
            (peligro) => {
              const checkState =
                peligroCheckStates[fase.id]?.[peligro.id];
              return checkState === true;
            }
          );

          return {
            ...fase,
            tipo: 'ENTORNO' as const,
            peligros: peligrosSeleccionados,
          };
        });

        logger.log('FasesPersonalesData preparadas, cantidad:', fasesPersonalesData?.length);
        logger.log('FasesEntornoData preparadas, cantidad:', fasesEntornoData?.length);

        const datosParaEnviar = {
          idSolicitud,
          fases: [...fasesPersonalesData, ...fasesEntornoData],
          observaciones: observaciones || '',
          fechaCreacion: new Date(),
        };

        logger.log('Datos finales a enviar (omitido por privacidad)');
        logger.log(`=== FIN DEBUG ===`);

        // Llamada al backend
        const response = await saveOrUpdateAnalisisRiesgo(
          'faseAr/analisisRiesgo',
          datosParaEnviar
        );
        return response;
      } catch (error) {
        logger.error('Error al guardar análisis de riesgo:', logger.safeError(error));
        throw error;
      }
    },
    [fasesPersonales, fasesEntorno, saveOrUpdateAnalisisRiesgo]
  );

  // Función para cambiar el contexto entre fases personales y de entorno
  const setContextoFases = useCallback(
    (tipo: 'PERSONAL' | 'ENTORNO') => {
      if (tipo === 'PERSONAL') {
        setFases(fasesPersonales);
      } else {
        setFases(fasesEntorno);
      }
    },
    [fasesPersonales, fasesEntorno]
  );

  // Función para cargar el análisis de riesgo completo desde el backend
  const cargarAnalisisRiesgoCompleto = useCallback(
    async (idSolicitud: number) => {
      try {
        logger.log(
          'Cargando análisis de riesgo para solicitud:',
          idSolicitud
        );

        // Llamada real al backend
        const response = await findAll(
          `faseAr/analisisRiesgo/${idSolicitud}`
        );
        const dataDummyBackend = response.data; // Debe coincidir con AnalisisRiesgoRequestDTO

        logger.log('Datos cargados del backend (omitido por privacidad)');

        // Verificar que la respuesta tenga la estructura esperada
        if (!dataDummyBackend || !dataDummyBackend.fases) {
          logger.warn('Respuesta del backend no tiene la estructura esperada');
          return {
            fasesPersonales: [],
            fasesEntorno: [],
            peligroCheckStatesEntorno: {},
            observaciones: '',
            comentarioAnalistaSeguridad: '',
          };
        }

        // Procesar las fases separando por tipo
        const fasesPersonales = dataDummyBackend.fases
          .filter((fase: any) => fase.tipo === 'PERSONAL')
          .map((fase: any) => {
            // Obtener los valores precargados del backend
            const exposicion = Number(
              fase.evaluacionRiesgo?.exposicion || 0.3
            );
            const probabilidad = Number(
              fase.evaluacionRiesgo?.probabilidad || 0.6
            );
            const consecuencia = Number(
              fase.evaluacionRiesgo?.consecuencia || 7
            );

            // Recalcular el riesgo y nivel de riesgo cuando hay valores precargados
            let riesgoCalculado: number;
            let nivelRiesgoCalculado: string;

            if (
              exposicion !== 0 &&
              probabilidad !== 0 &&
              consecuencia !== 0
            ) {
              // Si hay valores precargados, recalcular
              const resultado = calcularRiesgo(
                exposicion,
                probabilidad,
                consecuencia
              );
              riesgoCalculado = resultado.riesgo;
              nivelRiesgoCalculado = resultado.nivelRiesgo;
              logger.log(
                `=== RECÁLCULO AUTOMÁTICO FASE PERSONAL ${fase.id} ===`
              );
              logger.log(
                `Valores precargados: Exp=${exposicion}, Prob=${probabilidad}, Cons=${consecuencia}`
              );
              logger.log(
                `Resultado recalculado: Riesgo=${riesgoCalculado.toFixed(
                  2
                )}, Nivel=${nivelRiesgoCalculado}`
              );
            } else {
              // Si no hay valores precargados, usar valores por defecto o del backend
              riesgoCalculado = Number(
                fase.evaluacionRiesgo?.riesgo || 0
              );
              nivelRiesgoCalculado =
                fase.evaluacionRiesgo?.nivelRiesgo || 'MENOR';
              logger.log(
                `=== SIN RECÁLCULO FASE PERSONAL ${fase.id} ===`
              );
              logger.log(
                `Usando valores directos del backend: Riesgo=${riesgoCalculado}, Nivel=${nivelRiesgoCalculado}`
              );
            }

            return {
              id: fase.id,
              nroFase: fase.nroFase || 1,
              descripcion: fase.descripcion,
              peligros: fase.peligros.map((peligro: any) => ({
                id: peligro.id,
                nombre: peligro.nombre,
                barreraAdministrativa:
                  peligro.barreraAdministrativa || '',
                barreraFisica: peligro.barreraFisica || '',
                eppEspecifico: peligro.eppEspecifico || '',
                backendPeligroId: peligro.backendPeligroId,
                usuarioUltimaEdicion: peligro.usuarioUltimaEdicion || '',
              })),
              evaluacionRiesgo: {
                exposicion,
                probabilidad,
                consecuencia,
                riesgo: riesgoCalculado,
                nivelRiesgo: nivelRiesgoCalculado,
              },
            };
          });

        const fasesEntorno = dataDummyBackend.fases
          .filter((fase: any) => fase.tipo === 'ENTORNO')
          .map((fase: any) => {
            // Obtener los valores precargados del backend
            const exposicion = Number(
              fase.evaluacionRiesgo?.exposicion || 0.3
            );
            const probabilidad = Number(
              fase.evaluacionRiesgo?.probabilidad || 0.6
            );
            const consecuencia = Number(
              fase.evaluacionRiesgo?.consecuencia || 7
            );

            // Recalcular el riesgo y nivel de riesgo cuando hay valores precargados
            let riesgoCalculado: number;
            let nivelRiesgoCalculado: string;

            if (
              exposicion !== 0 &&
              probabilidad !== 0 &&
              consecuencia !== 0
            ) {
              // Si hay valores precargados, recalcular
              const resultado = calcularRiesgo(
                exposicion,
                probabilidad,
                consecuencia
              );
              riesgoCalculado = resultado.riesgo;
              nivelRiesgoCalculado = resultado.nivelRiesgo;
              logger.log(
                `=== RECÁLCULO AUTOMÁTICO FASE ENTORNO ${fase.id} ===`
              );
              logger.log(
                `Valores precargados: Exp=${exposicion}, Prob=${probabilidad}, Cons=${consecuencia}`
              );
              logger.log(
                `Resultado recalculado: Riesgo=${riesgoCalculado.toFixed(
                  2
                )}, Nivel=${nivelRiesgoCalculado}`
              );
            } else {
              // Si no hay valores precargados, usar valores por defecto o del backend
              riesgoCalculado = Number(
                fase.evaluacionRiesgo?.riesgo || 0
              );
              nivelRiesgoCalculado =
                fase.evaluacionRiesgo?.nivelRiesgo || 'MENOR';
              logger.log(
                `=== SIN RECÁLCULO FASE ENTORNO ${fase.id} ===`
              );
              logger.log(
                `Usando valores directos del backend: Riesgo=${riesgoCalculado}, Nivel=${nivelRiesgoCalculado}`
              );
            }

            return {
              id: fase.id,
              nroFase: fase.nroFase || 1,
              descripcion: fase.descripcion,
              peligros: fase.peligros.map((peligro: any) => ({
                id: peligro.id,
                nombre: peligro.nombre,
                barreraAdministrativa:
                  peligro.barreraAdministrativa || '',
                barreraFisica: peligro.barreraFisica || '',
                eppEspecifico: peligro.eppEspecifico || '',
                backendPeligroId: peligro.backendPeligroId,
                aplica: peligro.aplica,
                usuarioUltimaEdicion: peligro.usuarioUltimaEdicion || '',
              })),
              evaluacionRiesgo: {
                exposicion,
                probabilidad,
                consecuencia,
                riesgo: riesgoCalculado,
                nivelRiesgo: nivelRiesgoCalculado,
              },
            };
          });

        // Construir peligroCheckStatesEntorno desde el campo 'aplica'
        const peligroCheckStatesEntorno: {
          [faseId: number]: { [peligroId: number]: boolean };
        } = {};
        dataDummyBackend.fases
          .filter((fase: any) => fase.tipo === 'ENTORNO')
          .forEach((fase: any) => {
            peligroCheckStatesEntorno[fase.id] = {};
            fase.peligros.forEach((peligro: any) => {
              if (
                peligro.aplica !== null &&
                peligro.aplica !== undefined
              ) {
                peligroCheckStatesEntorno[fase.id][peligro.id] =
                  peligro.aplica;
              }
            });
          });

        // Actualizar estados con los datos procesados
        if (fasesPersonales.length > 0) {
          setFasesPersonales(fasesPersonales);
          setFases(fasesPersonales); // Establecer como fases principales por defecto
        }

        if (fasesEntorno.length > 0) {
          setFasesEntorno(fasesEntorno);
        }

        logger.log('Datos procesados (omitido por privacidad)');

        // Retornar los datos para que el componente padre pueda usarlos
        return {
          fasesPersonales: fasesPersonales || [],
          fasesEntorno: fasesEntorno || [],
          peligroCheckStatesEntorno: peligroCheckStatesEntorno || {},
          observaciones: dataDummyBackend.observaciones || '',
          comentarioAnalistaSeguridad:
            dataDummyBackend.comentarioAnalistaSeguridad || '',
        };
      } catch (error) {
        logger.error(
          'Error al cargar análisis de riesgo desde el backend:',
          logger.safeError(error)
        );

        // En caso de error, mostrar mensaje al usuario
        if (error instanceof Error) {
          logger.error('Detalles del error:', error.message);
        }

        // Retornar datos vacíos en caso de error
        return {
          fasesPersonales: [],
          fasesEntorno: [],
          peligroCheckStatesEntorno: {},
          observaciones: '',
          comentarioAnalistaSeguridad: '',
        };
      }
    },
    [setFasesPersonales, setFasesEntorno, setFases, calcularRiesgo]
  );

  // Función para actualizar fases según el contexto
  const actualizarFasesEnContexto = useCallback(
    (tipo: 'PERSONAL' | 'ENTORNO', nuevasFases: Fase[]) => {
      if (tipo === 'PERSONAL') {
        setFasesPersonales(nuevasFases);
        setFases(nuevasFases);
      } else {
        setFasesEntorno(nuevasFases);
        setFases(nuevasFases);
      }
    },
    []
  );

  return {
    // Estado
    analisisRiesgoList,
    analisisRiesgoSelected,
    fases,
    fasesPersonales,
    fasesEntorno,
    peligroSelected,
    peligroCheckStatesEntorno,

    // Acciones
    setFases,
    setFasesPersonales,
    setFasesEntorno,
    setPeligroCheckStatesEntorno,
    calcularRiesgo,
    actualizarRiesgoEvaluacion,
    agregarFase,
    editarFase,
    eliminarFase,
    agregarPeligro,
    editarPeligro,
    eliminarPeligro,
    handlePeligroSelected,
    eliminarPeligroById,
    saveOrUpdatePeligro,
    buscarAnalisisRiesgoByIdSolicitud,
    saveOrUpdateAnalisisRiesgo,
    handleAnalisisRiesgoSelected,
    guardarAnalisisRiesgo,
    cargarAnalisisRiesgoCompleto,
    setContextoFases,
    actualizarFasesEnContexto,
  };
};
